import { ComponentType } from "react";

import { OnboardingTourState, TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

export { TourStepId };

/** Props every tour step body receives — replacements must keep this contract. */
export interface StepBodyProps {
	tourState: OnboardingTourState;
}

/** Extra context available when a step derives its continue-button label. */
export interface ContinueLabelContext {
	state: OnboardingTourState;
	/** Active pipeline stage (0..2) inside the HOW_IT_WORKS step. */
	activeHowItWorksStage: number;
}

/**
 * Registry entry for one tour step. Another package replaces a placeholder by
 * swapping `BodyComponent` in constants/stepRegistry.ts — the shell, footer
 * and rail stay untouched.
 */
export interface StepDefinition {
	id: TourStepId;
	/** i18n key of the step's eyebrow line; interpolated with { current, total }. */
	eyebrowKey: string;
	titleKey: string;
	/**
	 * Short name of the step in the rail. Defaults to `titleKey` — steps whose
	 * panel title is a sentence about the step's content ("Signalist has identified
	 * 6 company segments for you") need a label that still reads as a step name in
	 * the list, and one that carries no `{{count}}` to interpolate.
	 */
	railLabelKey?: string;
	descKey: string;
	/** i18n key of the rail chat-note text typed out on first visit. */
	railNoteKey: string;
	/** Variant-aware rail note for the INVITED "Welcome tour" (falls back to railNoteKey). */
	railNoteKeyInvited?: string;
	BodyComponent: ComponentType<StepBodyProps>;
	/**
	 * Short i18n key used when this step is named as the *next* step on the
	 * previous step's "Next: …" continue button. Falls back to `titleKey` when
	 * the full panel title reads awkwardly as a button (e.g. LANGUAGE).
	 */
	nextLabelKey?: string;
	/**
	 * i18n key of the verb shown on the RUNNING loading screen (PREC-3719 §5.2),
	 * e.g. "onboardingTour.loading.verbs.building". Only steps whose generation
	 * can be in flight need one.
	 */
	loadingVerbKey?: string;
	/**
	 * True when this step's body has nothing to show until its generation
	 * finished — the screen is a pure review of the generated output. Drives the
	 * rail: only the *first* such step spins, the rest wait greyed out and
	 * unclickable. Steps that read fine meanwhile (the scoring explainer) leave
	 * it off, so one running generation no longer spins half the rail.
	 */
	contentBlockedWhileRunning?: boolean;
	/** Optional dynamic continue label; falls back to onboardingTour.footer.continue. */
	continueLabelKey?: (context: ContinueLabelContext) => string;
	/** Optional small hint rendered next to the continue button. */
	hintKey?: (context: ContinueLabelContext) => string | null;
}

/** The five scoring steps, in flow order — the simplified lead-scoring setup. */
export const SCORING_STEPS: TourStepId[] = [
	TourStepId.SCORING_INTRO,
	TourStepId.SCORING_SEGMENTS,
	TourStepId.SCORING_ROLES,
	TourStepId.SCORING_GRADING,
	TourStepId.SCORING_ACTIVATE,
];

/**
 * The steps the tour walks through (mirrors ONBOARDING_TOUR_STEP_ORDER in the
 * API — keep the two in sync). The Knowledge Base review, Deep Research and the
 * copy tuning stay background work; Create-Knowledge-Base stays, because the
 * Knowledge Base is built from the answers the user confirms there.
 *
 * The five SCORING_* screens come last (PREC-3861): they are the only ones that
 * cannot render before their generation finished, so everything the user can do
 * meanwhile is walked first and the scoring wait is spent instead of watched.
 */
export const STEP_ORDER: TourStepId[] = [
	TourStepId.HOW_IT_WORKS,
	TourStepId.CREATE_KNOWLEDGE,
	TourStepId.CONNECT,
	TourStepId.EXTENSION,
	TourStepId.LANGUAGE,
	...SCORING_STEPS,
];

/** Number of pipeline stages inside the HOW_IT_WORKS step. */
export const HOW_IT_WORKS_STAGE_COUNT = 3;
